import { forwardRef } from 'react';
import type { InputHTMLAttributes } from 'react';
import { cx } from './cx';
import { useFieldControl } from './Field';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Right-align and set in the record face. Use for money and counts. */
  numeric?: boolean;
  /** Set in the record face without right-aligning. Dates, references, citations. */
  mono?: boolean;
}

/**
 * 44px, hairline, 5px radius, surface fill. Focus is the 2px green outline.
 *
 * 16px text below 640px, 15px above. iOS Safari zooms the whole page into
 * any focused field set under 16px and does not zoom back out, so every
 * form on an iPhone started with a lurch sideways. Select, Textarea and
 * MoneyInput share this, so they are fixed with it.
 */
export const controlBase =
  'w-full rounded-ct-sm border border-line-control bg-paper-card px-3 py-2 text-[16px] leading-6 sm:text-sm text-ink min-h-tap shadow-none ' +
  'placeholder:text-ink-3 transition-colors duration-state ease-ct ' +
  'outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest ' +
  'disabled:opacity-60 disabled:cursor-not-allowed aria-[invalid=true]:border-danger';

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, numeric, mono, id, ...rest },
  ref,
) {
  const field = useFieldControl(id);
  return (
    <input
      ref={ref}
      {...field}
      className={cx(controlBase, (numeric || mono) && 'record', numeric && 'text-right', className)}
      {...rest}
    />
  );
});
